import { useState, useRef, useCallback } from 'react';
import { chatService } from '../../../services/chat.service';
import posthog from '../../../posthog';

export interface ChatMessage {
  id: string;
  role: 'human' | 'assistant';
  content: string;
  tool_used?: string | null;
  isStreaming?: boolean;
  activeTool?: string | null;
}

interface UseChatStreamOptions {
  sessionId: string | null;
  onSessionCreated?: (sessionId: string) => void;
  onTitleReady?: () => void;
}

export function useChatStream({ onSessionCreated, onTitleReady }: UseChatStreamOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const appendMessage = (msg: ChatMessage) =>
    setMessages((prev) => [...prev, msg]);

  const updateLastAssistant = (updater: (msg: ChatMessage) => ChatMessage) =>
    setMessages((prev) => {
      const idx = [...prev].reverse().findIndex((m) => m.role === 'assistant');
      if (idx === -1) return prev;
      const realIdx = prev.length - 1 - idx;
      return prev.map((m, i) => (i === realIdx ? updater(m) : m));
    });

  const send = useCallback(
    (text: string, activeSessionId: string, llmProvider: 'nvidia' | 'claude' | 'llama' | 'deepseek' = 'nvidia') => {
      if (isStreaming) return;

      const userMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'human',
        content: text,
      };
      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: '',
        isStreaming: true,
        activeTool: null,
      };

      appendMessage(userMsg);
      appendMessage(assistantMsg);
      posthog.capture('chat_message_sent', { llm_provider: llmProvider });
      setIsStreaming(true);

      abortRef.current = chatService.streamMessage(activeSessionId, text, llmProvider, {
        onSession: (sid) => onSessionCreated?.(sid),
        onToken: (delta) =>
          updateLastAssistant((m) => ({ ...m, content: m.content + delta })),
        onToolCall: (tool) =>
          updateLastAssistant((m) => ({ ...m, activeTool: tool })),
        onToolDone: (tool) =>
          updateLastAssistant((m) => ({ ...m, activeTool: null, tool_used: tool })),
        onDone: () => {
          updateLastAssistant((m) => ({ ...m, isStreaming: false, activeTool: null }));
          setIsStreaming(false);
          // Give time for title to be generated, then refresh
          setTimeout(() => onTitleReady?.(), 2500);
        },
        onError: () => {
          updateLastAssistant((m) => ({
            ...m,
            isStreaming: false,
            activeTool: null,
            content: m.content || 'Sorry, something went wrong. Please try again.',
          }));
          setIsStreaming(false);
        },
      });
    },
    [isStreaming, onSessionCreated, onTitleReady]
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
    updateLastAssistant((m) => ({ ...m, isStreaming: false, activeTool: null }));
  }, []);

  const loadMessages = useCallback((msgs: { role: 'human' | 'assistant'; content: string; tool_used?: string | null }[]) => {
    setMessages(
      msgs.map((m) => ({
        id: crypto.randomUUID(),
        role: m.role,
        content: m.content,
        tool_used: m.tool_used,
      }))
    );
  }, []);

  const clearMessages = useCallback(() => setMessages([]), []);

  return { messages, isStreaming, send, stop, loadMessages, clearMessages };
}
